import { NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import {
  LayoutDashboard, FileEdit, Map, MapPin, Newspaper, Quote, Inbox, Home,
  Settings, Users, LogOut, ExternalLink, HelpCircle, Image as ImageIcon,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/brand";

const NAV = [
  { to: "/cms", label: "Overview", icon: LayoutDashboard, end: true },
  { section: "Content" },
  { to: "/cms/pages", label: "Site Pages", icon: FileEdit },
  { to: "/cms/homepage", label: "Homepage", icon: Home },
  { to: "/cms/tours", label: "Tours", icon: Map },
  { to: "/cms/destinations", label: "Destinations", icon: MapPin },
  { to: "/cms/blog", label: "Blog", icon: Newspaper },
  { to: "/cms/testimonials", label: "Testimonials", icon: Quote },
  { to: "/cms/faqs", label: "FAQ Items", icon: HelpCircle },
  { section: "Operations" },
  { to: "/cms/enquiries", label: "Enquiries", icon: Inbox },
  { to: "/cms/media", label: "Media Library", icon: ImageIcon },
  { section: "System" },
  { to: "/cms/settings", label: "Settings & SEO", icon: Settings },
  { to: "/cms/users", label: "Users", icon: Users, adminOnly: true },
];

export default function CmsLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  if (user === null) return <div className="min-h-screen grid place-items-center bg-[#f7f5f0] text-[#5a4b4b]">Loading…</div>;
  if (user === false) return <Navigate to="/cms/login" replace />;

  const doLogout = async () => { await logout(); nav("/cms/login"); };

  return (
    <div className="min-h-screen flex bg-[#f7f5f0]">
      {/* Sidebar — lighter editor theme, distinct from /admin */}
      <aside className="w-72 shrink-0 bg-white border-r border-[#e5dfd2] flex flex-col fixed inset-y-0 left-0 z-40">
        <div className="px-6 py-6 border-b border-[#e5dfd2]">
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Legacy India Travel" data-testid="cms-sidebar-logo" className="w-10 h-10 object-contain rounded-full" />
            <div>
              <div className="font-serif text-lg text-[#3b1a1a] leading-tight">Legacy India Travel</div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#14523d] font-semibold">Content Studio</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-3">
          {NAV.filter((n) => !n.adminOnly || user.role === "admin").map((n, i) =>
            n.section ? (
              <div key={`sec-${i}`} className="px-6 mt-4 mb-1 text-[10px] tracking-[0.28em] uppercase text-[#9c8e77] font-semibold">{n.section}</div>
            ) : (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`cms-nav-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 mx-3 px-3 py-2.5 text-sm rounded-md transition-colors ${
                    isActive
                      ? "bg-[#14523d] text-white"
                      : "text-[#3b1a1a] hover:bg-[#faf7ee]"
                  }`
                }
              >
                <n.icon className="w-4 h-4" /> {n.label}
              </NavLink>
            )
          )}
        </nav>
        <div className="border-t border-[#e5dfd2] p-4 space-y-2">
          <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs text-[#5a4b4b] hover:text-[#7c1d1d] px-2 py-2">
            <ExternalLink className="w-4 h-4" /> View public website
          </a>
          <div className="px-2 text-[11px] text-[#8a7c66]">{user.name} · {user.role}</div>
          <button
            onClick={doLogout}
            data-testid="cms-logout"
            className="w-full flex items-center gap-2 text-sm text-[#7c1d1d] hover:text-white hover:bg-[#7c1d1d] border border-[#d4af37]/40 px-3 py-2.5 transition-colors rounded-md"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Content */}
      <div className="flex-1 ml-72 min-w-0">
        <div className="p-6 sm:p-10 max-w-[1200px]">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
